import { useAuth0 } from '@auth0/auth0-react'
import { RouteComponentProps } from 'react-router'
import * as React from 'react'
import Preloader from '../../common/components/Preloader'
import FourOhFourPage from '../../common/FourOhFourPage'
import { clone } from '../../common/helpers/clone'
import { SequenceDefinition } from '../../common/models'
import {
  getCarrierAccount,
  updateCarrierAccount,
} from '../../common/proxy/carrier-accounts-store'
import { getCaasProfile } from '../../common/proxy/carrier-services-store'
import { getSequenceDirect } from '../../common/proxy/sequences-store'
import { useLogisticsLocation } from '../../locations/LocationContext'
import ViewCarrierAccount, { PreloadedData } from './ViewCarrierAccount'

export interface Props
  extends RouteComponentProps<{
    carrierAccountId: string
  }> {}

export default function ViewCarrierAccountPageContainer(props: Props) {
  const { getAccessTokenSilently, user } = useAuth0()
  const [loading, setLoading] = React.useState(true)
  const [data, setData] = React.useState<PreloadedData>()
  const { logisticsLocation } = useLogisticsLocation()

  React.useEffect(() => {
    const fetch = async () => {
      setLoading(true)
      const accessToken = await getAccessTokenSilently()
      const carrierAccountId = props.match.params.carrierAccountId
      const [carrierAccount] = await Promise.all([
        getCarrierAccount(accessToken, carrierAccountId),
      ])

      if (carrierAccount === undefined) {
        return
      }

      const sequenceDefinitionPromises = Promise.all(
        Object.keys(carrierAccount.sequences!).map(async key => ({
          key,
          sequenceDefinition: await getSequenceDirect(
            accessToken,
            user,
            carrierAccount.sequences![key]._links!.self.href
          ),
        }))
      )

      const [profile, sequenceDefinitions] = await Promise.all([
        getCaasProfile(accessToken, carrierAccount.carrierKey),
        sequenceDefinitionPromises,
      ])

      const sequenceDefinitionPairs = sequenceDefinitions.map(sequenceData => ({
        sequence: profile.sequences!.find(s => s.key === sequenceData.key)!,
        sequenceDefinition: {
          ...sequenceData.sequenceDefinition,
          _metadata: {
            sequenceKey: sequenceData.key,
            isShared:
              (profile.sharedSequences || {})[sequenceData.key] !== undefined,
          },
        } as SequenceDefinition,
      }))

      setData({
        carrierAccount,
        profile,
        sequenceDefinitionPairs,
      })
      setLoading(false)
    }
    fetch()
  }, [
    getAccessTokenSilently,
    logisticsLocation,
    props.match.params.carrierAccountId,
    user,
  ])

  if (loading) {
    return <Preloader />
  }

  if (!data) {
    return <FourOhFourPage />
  }

  const updateCarrierAccountName = async (
    updatedCarrierAccountName: string
  ) => {
    const accessToken = await getAccessTokenSilently()
    const newCarrierAccount = clone(data.carrierAccount)
    newCarrierAccount.name = updatedCarrierAccountName

    const updated = await updateCarrierAccount(
      accessToken,
      data.carrierAccount,
      newCarrierAccount
    )

    newCarrierAccount.etag = updated
    setData({ ...data, carrierAccount: newCarrierAccount })
  }

  return (
    <ViewCarrierAccount
      {...props}
      {...data}
      updateCarrierAccountName={updateCarrierAccountName}
    />
  )
}
